import { useCallback } from 'react';
import { TagPickerOnOptionSelectData } from '@fluentui/react-components';
import { TagPickerItem, noMatchesToken } from './GenericTagPicker.types';

export interface UseTagPickerSelectionProps<T extends TagPickerItem> {
    items: T[];
    selectedItems: T[];
    selectedItem?: T | null;
    multiSelect: boolean;
    onItemChange?: (item: T | null) => void;
    onItemsChange?: (items: T[]) => void;
    setQuery: (query: string) => void;
}

export const useTagPickerSelection = <T extends TagPickerItem>({
    items,
    selectedItems,
    selectedItem,
    multiSelect,
    onItemChange,
    onItemsChange,
    setQuery
}: UseTagPickerSelectionProps<T>) => {

    // Dismiss handler for removing specific items
    const handleDismiss = useCallback((itemId: string) => {
        if (multiSelect && onItemsChange) {
            // Remove the item with the specified ID
            onItemsChange(selectedItems.filter(item => item.id.toString() !== itemId));
        } else if (!multiSelect && onItemChange && selectedItem?.id.toString() === itemId) {
            // For single select, clear if it's the selected item
            onItemChange(null);
        }
    }, [multiSelect, onItemsChange, onItemChange, selectedItems, selectedItem]);

    // FluentUI v9 TagPicker selection handler (for dropdown selections)
    const handleOptionSelect = useCallback((_event: unknown, data: TagPickerOnOptionSelectData) => {
        setQuery("");
        if (data.value === noMatchesToken) return;

        const clickedItemId = data.value;
        if (!clickedItemId) return;

        if (multiSelect && onItemsChange) {
            const currentSelectedIds = selectedItems.map(item => item.id.toString());

            if (currentSelectedIds.includes(clickedItemId)) {
                // REMOVE action - item was selected, now remove it
                onItemsChange(selectedItems.filter(item => item.id.toString() !== clickedItemId));
            } else {
                // ADD action - item was not selected, now add it
                const clickedItem = items.find(item => item.id.toString() === clickedItemId);
                if (!clickedItem) return;

                onItemsChange([...selectedItems, clickedItem]);
            }
        } else if (!multiSelect && onItemChange) {
            // Single select logic
            const currentSelectedId = selectedItem?.id.toString();

            if (currentSelectedId === clickedItemId) {
                // REMOVE action - deselect current item
                onItemChange(null);
            } else {
                // ADD action - select new item
                const clickedItem = items.find(item => item.id.toString() === clickedItemId);
                onItemChange(clickedItem ?? null);
            }
        }
    }, [setQuery, items, multiSelect, onItemsChange, onItemChange, selectedItems, selectedItem]);

    return {
        handleOptionSelect,
        handleDismiss
    };
};
